// <pos-my-shifts/> — 員工端：我的班表 + 請假申請（W13 MVP）
// 路徑：#/my-shifts（authed；任何角色，靠 RLS 而非 guard 篩資料）
//
// 資料面全靠 RLS（see 20260707000010_staff_self_scheduling.sql）：
//   - employees：emp_self_read 只回「profile_id = 我」那列 → 解析我的 employee_id
//   - schedules / shift_assignments：published 班表同 tenant 可讀（既有 policy）
//   - employee_time_off_requests：self insert/read/delete（requested 才能撤回）
// 未綁定 employees.profile_id 的帳號 → 顯示請店長綁定的指引。

import { getSupabase } from '../lib/supabase-client.js';
import { escapeHtml } from '../lib/utils/escape.js';

const WD_LABELS = ['日', '一', '二', '三', '四', '五', '六'];
const PRIORITY_LABELS = { must: '必須休', prefer: '希望休' };
const STATUS_LABELS = { requested: '待審核', approved: '已核准', rejected: '未核准' };

function fmtDate(iso) {
  // 用固定 +08:00 錨點取台北的星期幾（與裝置時區無關）
  const wd = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Taipei', weekday: 'short' })
    .format(new Date(`${iso}T00:00:00+08:00`));
  const WD_MAP = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return `${iso.slice(5).replace('-', '/')}（${WD_LABELS[WD_MAP[wd]]}）`;
}

function todayTaipei() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Taipei' }).format(new Date());
}

class PosMyShifts extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = {
      loading: true,
      error: null,
      me: null,          // 我的 employees 列（null = 尚未綁定）
      shifts: [],        // [{date, template_name, start_time, end_time, conflict_notes}]
      timeOff: [],       // 我的請假紀錄
    };
  }

  get tenantId() {
    return document.body.dataset.tenantId || null;
  }

  async connectedCallback() {
    this.render();
    await this.load();
  }

  async load() {
    const supabase = getSupabase();
    try {
      // 1) 我是哪個 employee（RLS 只會回自己那列）
      const { data: me, error: meErr } = await supabase.from('employees')
        .select('id, name, role')
        .limit(1).maybeSingle();
      if (meErr) throw meErr;
      this.state.me = me;

      if (me) {
        const today = todayTaipei();
        // 2) 未來 14 天內重疊的 published 班表
        const { data: schedules, error: sErr } = await supabase.from('schedules')
          .select('id, period_start, period_end, status')
          .eq('status', 'published')
          .gte('period_end', today)
          .order('period_start', { ascending: true })
          .limit(4);
        if (sErr) throw sErr;

        let shifts = [];
        if (schedules?.length) {
          const ids = schedules.map((s) => s.id);
          const [{ data: asg, error: aErr }, { data: tpls, error: tErr }] = await Promise.all([
            supabase.from('shift_assignments')
              .select('schedule_id, shift_template_id, date, conflict_notes')
              .in('schedule_id', ids)
              .eq('employee_id', me.id)
              .gte('date', today)
              .order('date', { ascending: true }),
            supabase.from('shift_templates')
              .select('id, name, start_time, end_time'),
          ]);
          if (aErr) throw aErr;
          if (tErr) throw tErr;
          const tplMap = new Map((tpls || []).map((t) => [t.id, t]));
          shifts = (asg || []).map((a) => {
            const t = tplMap.get(a.shift_template_id) || {};
            return {
              date: a.date,
              template_name: t.name || '班',
              start_time: (t.start_time || '').slice(0, 5),
              end_time: (t.end_time || '').slice(0, 5),
              conflict_notes: a.conflict_notes,
            };
          });
        }
        this.state.shifts = shifts;

        // 3) 我的請假紀錄（近的在前）
        // QA 建議 #15：含近 30 天歷史（員工想核對「上次請的假有沒有過」）
        const since = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
        const { data: reqs, error: rErr } = await supabase.from('employee_time_off_requests')
          .select('id, date, priority, status, note')
          .gte('date', since)
          .order('date', { ascending: false })
          .limit(60);
        if (rErr) throw rErr;
        this.state.timeOff = reqs || [];
      }
      this.state.error = null;
    } catch (err) {
      this.state.error = err.message_zh || err.message || '載入失敗';
    } finally {
      this.state.loading = false;
      this.render();
    }
  }

  async requestTimeOff() {
    const root = this.shadowRoot;
    const date = root.querySelector('#offDate')?.value;
    const priority = root.querySelector('#offPriority')?.value || 'prefer';
    const note = root.querySelector('#offNote')?.value.trim() || null;
    if (!date) return alert('請選日期');
    if (date < todayTaipei()) return alert('不能申請過去的日期');
    const { error } = await getSupabase().from('employee_time_off_requests').insert({
      tenant_id: this.tenantId,
      employee_id: this.state.me.id,
      date, priority, note,
      status: 'requested',
    });
    if (error) {
      return alert('申請失敗：' + (error.code === '23505' ? '這天已經申請過了' : error.message));
    }
    await this.load();
  }

  async withdrawTimeOff(id) {
    if (!confirm('撤回這筆請假申請？')) return;
    const { error } = await getSupabase().from('employee_time_off_requests')
      .delete().eq('id', id);
    if (error) return alert('撤回失敗：' + error.message);
    await this.load();
  }

  render() {
    const { loading, error, me, shifts, timeOff } = this.state;

    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; padding: var(--s-6) var(--s-4); max-width: 680px; margin: 0 auto; font-family: var(--font-sans); color: var(--fg-base); }
        h1 { font-size: 1.35rem; margin: 0 0 var(--s-5); }
        h2 { font-size: 1.05rem; margin: 0 0 var(--s-3); }
        .banner { padding: var(--s-3) var(--s-4); border-radius: var(--r-md); margin-bottom: var(--s-4); }
        .banner.err { background: rgba(239,68,68,.1); color: var(--danger, #ef4444); }
        .banner.loading { background: rgba(59,130,246,.08); color: var(--fg-muted); }
        .card { background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--r-lg); padding: var(--s-5); margin-bottom: var(--s-4); }
        .empty { color: var(--fg-muted); padding: var(--s-4) 0; text-align: center; }
        .shift { display: flex; align-items: center; gap: var(--s-4); padding: var(--s-3) 0; border-bottom: 1px dashed var(--border); }
        .shift:last-child { border-bottom: 0; }
        .shift__date { font-weight: 700; min-width: 96px; }
        .shift__name { flex: 1; }
        .shift__time { font-family: var(--font-mono); color: var(--brand-primary); }
        .req { display: flex; align-items: center; gap: var(--s-3); padding: var(--s-2) 0; border-bottom: 1px dashed var(--border); font-size: .92rem; }
        .req--past { opacity: .55; }
        .req:last-child { border-bottom: 0; }
        .badge { border-radius: var(--r-full); padding: 2px 10px; font-size: .78rem; }
        .badge--requested { background: rgba(196,125,0,.15); color: var(--brand-accent); }
        .badge--approved { background: rgba(34,197,94,.15); color: #16a34a; }
        .badge--rejected { background: rgba(239,68,68,.12); color: var(--danger, #ef4444); }
        .form-row { display: flex; flex-wrap: wrap; gap: var(--s-2); margin-top: var(--s-3); }
        input, select { background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--r-md); color: var(--fg-base); padding: var(--s-2) var(--s-3); font-family: inherit; }
        input[type=text] { flex: 1; min-width: 140px; }
        button { border: 0; border-radius: var(--r-md); padding: var(--s-2) var(--s-4); cursor: pointer; font-family: inherit; }
        .primary { background: linear-gradient(135deg, var(--brand-primary), var(--brand-accent)); color: var(--fg-on-brand, #fff); }
        .ghost { background: transparent; color: var(--fg-muted); border: 1px solid var(--border); }
        .note { color: var(--fg-muted); font-size: .85rem; }
        .btn-link { display: inline-block; margin-top: var(--s-3); color: var(--brand-primary); font-weight: 600; text-decoration: none; }
      </style>

      <h1>我的班表</h1>
      ${loading ? '<div class="banner loading">載入中…</div>' : ''}
      ${error ? `<div class="banner err">✗ ${escapeHtml(error)}</div>` : ''}

      ${!loading && !me ? `
        <div class="card">
          <h2>帳號還沒綁定員工資料</h2>
          ${document.body.dataset.role === 'owner' ? `
            <p class="note">你是店家擁有人。想讓自己也出現在班表上，到 <a href="#/settings/scheduling">設定 → 排班</a> 的員工列表，把「登入帳號」欄選成你自己就完成了。</p>
            <a class="btn-link" href="#/settings/scheduling">前往排班設定 →</a>
          ` : `
            <p class="note">請老闆到「設定 → 排班 → 員工」把你的登入帳號綁定到員工名單，綁定後就能在這裡看到自己的班表、線上請假。</p>
          `}
        </div>
      ` : ''}

      ${me ? `
        <div class="card">
          <h2>${escapeHtml(me.name)} 的近期班次</h2>
          ${shifts.length ? shifts.map((s) => `
            <div class="shift">
              <span class="shift__date">${fmtDate(s.date)}</span>
              <span class="shift__name">${escapeHtml(s.template_name)}</span>
              <span class="shift__time">${escapeHtml(s.start_time)}–${escapeHtml(s.end_time)}</span>
            </div>
          `).join('') : '<div class="empty">近期沒有已發布的班次</div>'}
        </div>

        <div class="card">
          <h2>請假申請</h2>
          ${timeOff.length ? [...timeOff].sort((a, b) => (a.date < b.date ? 1 : -1)).map((r) => `
            <div class="req ${r.date < todayTaipei() ? 'req--past' : ''}">
              <span class="shift__date">${fmtDate(r.date)}</span>
              <span>${PRIORITY_LABELS[r.priority] || ''}</span>
              <span class="badge badge--${escapeHtml(r.status)}">${STATUS_LABELS[r.status] || r.status}</span>
              <span class="note" style="flex:1">${escapeHtml(r.note || '')}</span>
              ${r.status === 'requested'
                ? `<button class="ghost" data-act="withdraw" data-id="${escapeHtml(r.id)}">撤回</button>` : ''}
            </div>
          `).join('') : '<div class="empty">目前沒有請假申請</div>'}
          <div class="form-row">
            <input id="offDate" type="date" min="${todayTaipei()}" />
            <select id="offPriority">
              <option value="prefer">希望休（盡量避開）</option>
              <option value="must">必須休（絕不排班）</option>
            </select>
            <input id="offNote" type="text" maxlength="100" placeholder="原因（選填）" />
            <button class="primary" data-act="request-off">送出申請</button>
          </div>
          <p class="note">送出後由店長審核；核准的請假會成為下次排班的硬約束。</p>
        </div>
      ` : ''}
    `;

    this.shadowRoot.querySelectorAll('[data-act]').forEach((el) => {
      el.addEventListener('click', () => {
        if (el.dataset.act === 'request-off') this.requestTimeOff();
        if (el.dataset.act === 'withdraw') this.withdrawTimeOff(el.dataset.id);
      });
    });
  }
}

customElements.define('pos-my-shifts', PosMyShifts);
